import { useEffect, useRef } from "react";

const ACTIONS = [
  {
    key: "complete",
    label: "Completed",
    color: "text-green-700",
    hoverBg: "hover:bg-green-50",
    activeColor: "active:bg-green-100",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
  {
    key: "cancel",
    label: "Cancelled",
    color: "text-red-600",
    hoverBg: "hover:bg-red-50",
    activeColor: "active:bg-red-100",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
  },
  {
    key: "reschedule",
    label: "Reschedule",
    color: "text-blue-700",
    hoverBg: "hover:bg-blue-50",
    activeColor: "active:bg-blue-100",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
];

/**
 * Professional status-action popover / bottom-sheet.
 *
 * @param {DOMRect}   anchorRect      getBoundingClientRect() of the clicked badge
 * @param {Function}  onSelect        (key) => void   — "complete" | "cancel" | "reschedule"
 * @param {Function}  onClose         () => void
 * @param {boolean}   isMobile        render as bottom-sheet instead of anchored dropdown
 * @param {string[]}  [visibleActions] optional subset of action keys to show
 */
export default function StatusActionMenu({ anchorRect, onSelect, onClose, isMobile, visibleActions }) {
  const menuRef = useRef(null);
  const actions = visibleActions ? ACTIONS.filter((a) => visibleActions.includes(a.key)) : ACTIONS;

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [onClose]);

  // Dismiss on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (actions.length === 0) return null;

  /* ── Mobile: bottom-sheet ── */
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30">
        <div
          ref={menuRef}
          className="w-full max-w-lg bg-white rounded-t-2xl shadow-2xl pb-6 pt-3 px-4"
          style={{ animation: "slideUp .2s ease-out" }}
        >
          <div className="w-10 h-1 rounded-full bg-gray-300 mx-auto mb-3" />
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
            Update Status
          </p>
          <div className="space-y-1">
            {actions.map((a) => (
              <button
                key={a.key}
                onClick={() => { onSelect(a.key); onClose(); }}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium ${a.color} ${a.hoverBg} ${a.activeColor} transition-colors`}
              >
                {a.icon}
                {a.label}
              </button>
            ))}
          </div>
          <button
            onClick={onClose}
            className="w-full mt-3 py-2.5 text-sm font-medium text-gray-500 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  /* ── Desktop: anchored dropdown ── */
  let top = 0;
  let left = 0;
  if (anchorRect) {
    top = anchorRect.bottom + 4;
    left = anchorRect.left;
    const menuWidth = 192;
    const menuHeight = actions.length * 40 + 36;
    if (left + menuWidth > window.innerWidth) left = window.innerWidth - menuWidth - 8;
    if (top + menuHeight > window.innerHeight) top = anchorRect.top - menuHeight - 4;
    if (left < 4) left = 4;
  }

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div
        ref={menuRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[180px]"
        style={{ position: "fixed", top, left }}
      >
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 pt-2 pb-1.5">
          Update Status
        </p>
        {actions.map((a) => (
          <button
            key={a.key}
            onClick={() => { onSelect(a.key); onClose(); }}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium ${a.color} ${a.hoverBg} transition-colors`}
          >
            {a.icon}
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}
